<script setup lang="ts">
import { type IValidationResult } from "@/interfaces";
import FileTrigger from "@/stories/form/FileTrigger/FileTrigger.vue";
import InputValidationError from "@/stories/form/InputValidationError/InputValidationError.vue";
import PixabayIcon from "@/assets/svg/pixabay.svg?component";
import { FileAcceptTypes, FileSizes } from "@/enums";

interface Props {
  name: string;
  pixabaySupport?: boolean;
  uploading?: boolean;
  validation?: IValidationResult | null;
  showTitle?: boolean;
  error?: boolean | null;
  disabled?: boolean;
  maxSize?: FileSizes;
}

withDefaults(defineProps<Props>(), {
  pixabaySupport: true,
  showTitle: true,
  disabled: false,
  maxSize: FileSizes.Max15MB,
});

defineEmits<{
  clickFromComputer: [];
  clickFromCloud: [];
  dropped: [FileList];
}>();
</script>

<template>
  <div class="image-trigger-container">
    <div class="gap-6 image-trigger-content">
      <FileTrigger
        icon="far-image"
        :uploading="uploading"
        :show-title="showTitle"
        :error="error"
        :disabled="disabled"
        :accept="FileAcceptTypes.OnlyImage"
        :maxSize="maxSize"
        @click="$emit('clickFromComputer')"
        @dropped="(files) => $emit('dropped', files)"
      />

      <div class="flex flex-col gap-2" v-if="pixabaySupport">
        <div class="h100 text-neutral1000">
          {{ $t("global.from_online_library") }}
        </div>
        <button
          type="button"
          class="bg-neutral1000 text-white flex justify-center items-center flex-col h-140 w-140 rounded-lg b300 pixabay-button"
          @click="$emit('clickFromCloud')"
        >
          <PixabayIcon width="5.125rem" height="3.375rem" />
        </button>
      </div>
    </div>
    <InputValidationError :name="name" :validation="validation" />
  </div>
</template>

<style lang="scss" scoped>
.image-trigger-container {
  container-type: inline-size;
  container-name: image-trigger;
}

.image-trigger-content {
  display: flex;
}

@container image-trigger (max-width: 25rem) {
  .image-trigger-content {
    flex-direction: column;
  }

  .pixabay-button {
    width: 100%;
  }
}
</style>
